import { useQuery, useSubscription } from '@apollo/client/react';
import { GET_BTC_SCHEDULE } from '@optools/services/operator-gateway/btc/queries';
import { BtcScheduleGame } from '@optools/services/operator-gateway/btc/types';
import { MEDIA_UPDATES } from '@optools/services/operator-gateway/subscriptions';
import { Sport } from '@optools/services/operator-gateway/types/generated/graphql';
import { ReactNode, useSearch } from '@tanstack/react-router';
import { createContext, useContext, useMemo } from 'react';

export interface BTCScheduleContextValues {
  loading: boolean;
  schedule: BtcScheduleGame[];
}

export const BTCScheduleContext = createContext<BTCScheduleContextValues | null>(null);

export const BTCScheduleContextProvider = ({ children }: { children: ReactNode }) => {
  const { date } = useSearch({ from: '/_auth/btc-tools/schedule' });

  const { data, loading } = useQuery(GET_BTC_SCHEDULE, {
    fetchPolicy: 'cache-and-network',
    variables: { date, sports: [Sport.MLB] },
  });

  const mediaIds =
    data?.schedule.flatMap(({ content }) => content.map(({ mediaId }) => mediaId)) || [];

  useSubscription(MEDIA_UPDATES, {
    context: { skipErrorNotification: true },
    fetchPolicy: 'network-only',
    shouldResubscribe: true,
    skip: !mediaIds.length,
    variables: { mediaIds },
  });

  const value = useMemo(
    () => ({ loading, schedule: data?.schedule ?? [] }),
    [loading, data?.schedule],
  );

  return <BTCScheduleContext.Provider value={value}>{children}</BTCScheduleContext.Provider>;
};

export const useBtcScheduleContext = () => {
  const context = useContext(BTCScheduleContext);
  if (!context) {
    throw new Error('useBtcScheduleContext must be used within a BTCScheduleContextProvider');
  }

  return context;
};
